// ------------------------------------------------------------------
// PURE MODULE: lazyload_queue.mod.js - V2 - Image Priority + Video Drop
// ------------------------------------------------------------------

export function LazyLoadQueue() {
    let instanceConfig = {};
    let activeQueues = new Map();

    return {
        init(customConfig = {}) {
            instanceConfig = { ...LazyLoadQueue.defaults, ...customConfig };
            const wrappers = document.querySelectorAll(instanceConfig.targets);

            if (wrappers.length === 0) return;

            // =========================================================
            // [THE DISPATCHER]: Dynamic Threshold IO
            // =========================================================
            const observer = new IntersectionObserver((entries, obs) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const wrapper = entry.target;
                        
                        if (!activeQueues.has(wrapper)) {
                            activeQueues.set(wrapper, createQueue(wrapper));
                        }
                        
                        obs.unobserve(wrapper);
                    }
                });
            }, { rootMargin: instanceConfig.rootMargin });

            wrappers.forEach(w => observer.observe(w));
        }
    };

    // =========================================================
    // [THE QUEUER]: Isolated Instance Logic
    // =========================================================
    function createQueue(wrapper) {
        // We strictly queue based on the fallback/main images
        const items = Array.from(wrapper.querySelectorAll(instanceConfig.itemSelector));
        if (items.length === 0) return null;

        const state = new Array(items.length).fill('idle'); 

        const loadItem = (index) => {
            if (!items[index] || state[index] !== 'idle') return Promise.resolve();
            
            state[index] = 'loading';
            const img = items[index];

            return new Promise((resolve) => {
                const finalize = () => {
                    state[index] = 'loaded';
                    img.classList.add(instanceConfig.loadedClass);
                    
                    // =========================================================
                    // [NEW]: The "Drop and Run" Video Hand-off
                    // =========================================================
                    // Look for a video element sharing the same direct parent wrapper
                    const parentBlock = img.closest(instanceConfig.mediaWrapperSelector) || img.parentElement;
                    const video = parentBlock.querySelector('video');
                    
                    if (video) {
                        const sources = video.querySelectorAll('source[data-src]');
                        sources.forEach(source => {
                            source.src = source.getAttribute('data-src');
                            source.removeAttribute('data-src');
                        });
                        
                        video.load(); // Native background fetch
                        
                       /*  // Optional: Smoothly swap visuals when video actually has frames ready
                        video.addEventListener('canplay', () => {
                            video.classList.add(instanceConfig.videoReadyClass);
                            // img.style.opacity = 0; // Or handle this via CSS targeting the class
                        }, { once: true }); */
						
video.addEventListener('canplay', () => {
    video.classList.add(instanceConfig.videoReadyClass);
    
    // THE FIX: Explicitly trigger playback once enough data is buffered
    const playPromise = video.play();
    if (playPromise !== undefined) {
        playPromise.catch(error => {
            // Silently catch autoplay restrictions if they occur
            console.warn("Video autoplay prevented by browser:", error);
        });
    }
}, { once: true });
						
                    }

                    // Release the queue immediately so the next slide can load!
                    resolve();
                };

                img.addEventListener('load', finalize, { once: true });
                img.addEventListener('error', finalize, { once: true }); // Prevent queue freezing on 404

                img.src = img.getAttribute('data-src');
                img.removeAttribute('data-src');
                
                if (img.decode) img.decode().catch(() => {});
            });
        };

        // ---------------------------------------------------------
        // LANE 1: The Background Crawler (Sequential)
        // ---------------------------------------------------------
        const crawl = async () => {
            for (let i = 0; i < items.length; i++) {
                if (state[i] === 'idle') {
                    await loadItem(i); 
                }
            }
        };

        // ---------------------------------------------------------
        // LANE 2: The VIP Override (MutationObserver)
        // ---------------------------------------------------------
        const mutObs = new MutationObserver((mutations) => {
            mutations.forEach(mut => {
                if (mut.attributeName === instanceConfig.activeIndexAttr) {
                    const activeIdx = parseInt(wrapper.getAttribute(instanceConfig.activeIndexAttr), 10);
                    
                    if (!isNaN(activeIdx) && state[activeIdx] === 'idle') {
                        loadItem(activeIdx); 
                    }
                }
            });
        });

        mutObs.observe(wrapper, { attributes: true, attributeFilter: [instanceConfig.activeIndexAttr] });

        crawl();

        return { wrapper, items, state };
    }
}

// Default Configuration
LazyLoadQueue.defaults = {
    targets: "[data-lazy-queue]",          
    itemSelector: "img[data-src]",         
//    mediaWrapperSelector: ".lazy-reveal-wrapper", // Defines the boundary for finding sibling videos
    mediaWrapperSelector: ".lazyvid", // Defines the boundary for finding sibling videos
    rootMargin: "1500px 0px",              
    activeIndexAttr: "data-active-index",  
    loadedClass: "is-loaded",
    videoReadyClass: "video-is-ready"      // Hook for CSS transitions
};